<template>
  <div id="chemical-search-wrapper" data-test="chemical-search-wrapper">
    <b-form-group
      class="chemical-search-by-label"
      :label="searchByLabel"
      label-for="chemical-search-type"
    >
      <raft-autocomplete
        v-if="!useLocalChemical"
        :key="fieldConfig.field_name"
        field-class="entity-reference-field"
        ref="autocomplete"
        :id="fieldConfig.field_name"
        :name="fieldConfig.field_name"
        :default-value="selectedItem"
        :label="fieldConfig.label"
        element-id="chemical-search-autocomplete"
        :placeholder="getPlaceholder()"
        :source="debounceChemicalLookup"
        :results-display="formattedResultsDisplay"
        :results-formatter="formatResults"
        :results-property="resultsProperty"
        input-class="form-control"
        @selected="updateSelected"
        @clear="clearSelected()"
        :disabled="disabled"
        :field-required="required"
        :show-label="false"
        :is-chemsnap-enabled="isChemsnapEnabled"
        :chemsnap-found="defaultLookupValue"
        @search="updateSearch"
        :border-status="_status"
      >
        <template #helptext>
          <template v-if="searchResultsCount === 0 && hasSearched">
            No matches found.
            <a href="#" @click="$emit('search-unlinked-chemicals', searchTerm)"
              >Search unlinked chemicals</a
            >?
          </template>
          <template v-if="searchResultsCount > 0 && hasSearched">
            {{ selectedOrChooseHelpText }}
            <a href="#" @click.prevent="$refs.autocomplete.open = true">{{
              searchResultsLinkText
            }}</a
            >.
            <a
              href="#"
              @click.prevent="$emit('search-unlinked-chemicals', searchTerm)"
              >Search unlinked chemicals</a
            >?
          </template>
        </template>
        <template #radiogroup v-if="horizontalOptions">
          <div class="horizontal-search-by-chemical">
            <div class="chemical-title">
              <span>Chemical</span>
            </div>
            <div class="chemical-options">
              <div
                class="field-label field__label required"
                title="This field is required."
              >
                <span>By</span>
              </div>
              <b-form-radio-group
                v-if="optionsAbove && !useLocalChemical"
                id="chemical-search-type"
                v-model="chemicalSearchType"
                :options="searchOptions"
                name="radio-options"
              />
            </div>
          </div>
        </template>
        <template #radiogroup v-else>
          <div
            class="field-label field__label required"
            title="This field is required."
          >
            {{ getSearchLabel() }}:
            <sup>*</sup>
          </div>
          <b-form-radio-group
            v-if="optionsAbove && !useLocalChemical"
            id="chemical-search-type"
            v-model="chemicalSearchType"
            :options="searchOptions"
            name="radio-options"
            :class="{
              'chemical-search-options-inline-above': optionsInline,
              'chemical-search-options-stacked': !optionsInline,
            }"
            :button="true"
          />
        </template>
        <template #input v-if="chemsnapFound && !showSearchInput">
          <ChemSnapMatchBadge
            :result-value="chemsnapFound"
            @change-chemsnap-match="handleChemsnapMatchChange"
          />
        </template>
      </raft-autocomplete>
      <div id="unlinked-chemical-selection" v-else>
        <div class="column unlinked-pair">
          <strong>Chemical Name</strong>
          <div>{{ localChemicalTitle }}</div>
        </div>
        <FilterClearButton
          v-if="allowDelete"
          class="autocomplete__icon autocomplete--clear"
          @click="clearSelected()"
        />
      </div>
      <b-form-radio-group
        v-if="!optionsAbove"
        id="chemical-search-type"
        v-model="chemicalSearchType"
        :options="searchOptions"
        name="radio-options"
        class="chemical-search-options-inline"
      />
      <slot name="extra" />
    </b-form-group>
    <div class="chemical-search-unnamed-slot">
      <slot />
    </div>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import FilterClearButton from "@/components/filters/FilterClearButton.vue";
import trash from "@/images/trash_filled.svg";
import { ElasticsearchAuthManager } from "../../lib/Elasticsearch/ElasticsearchAuthManager";
import { ElasticsearchClient } from "../../lib/Elasticsearch/ElasticsearchClient";
import { useDrupalSettings } from "../../composables/useDrupalSettings";
import ErrorStateLinks from "./ErrorStateLinks.vue";
import RaftAutocomplete from "./RaftAutocomplete.vue";
import ChemSnapMatchBadge from "./ChemSnapMatchBadge.vue";

const { config, updateChemicalSearchToken } = useDrupalSettings();

export default {
  name: "ChemicalSearch",
  components: {
    RaftAutocomplete,
    ChemSnapMatchBadge,
    FilterClearButton,
  },
  data() {
    return {
      errors: [],
      selectedItem: {},
      elasticAuthManager: null,
      elasticSearchClient: null,
      debounceChemicalLookup: debounce(this.chemicalLookup, 300, {
        leading: false,
      }),
      chemicalSearchType: "suggest",
      lookupValue: "",
      searchTerm: "",
      searchResults: [],
      searchType: "linked",
      trashSrc: trash,
      hasSearched: false,
    };
  },
  props: {
    fieldConfig: { type: Object, required: true },
    value: { type: Object, default: null },
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    loseNameContains: { type: Boolean, default: false },
    showLabel: { type: Boolean, default: true },
    allowDelete: { type: Boolean, default: false },
    fieldIndex: { type: Number, default: 1 },
    optionsAbove: { type: Boolean, default: false },
    optionsInline: { type: Boolean, default: false },
    localChemical: { type: Object, default: null },
    chemicalSearchTypeDefault: { type: String, default: "suggest" },
    defaultLookupValue: { type: String, default: "" },
    horizontalOptions: { type: Boolean, default: false },
    isChemsnapEnabled: { type: Boolean, default: false },
    chemSnapData: { type: Object, default: null },
    showSearchInput: { type: Boolean, default: true },
    status: { type: String, default: null },
  },
  methods: {
    updateAuthToken: function () {
      setInterval(async () => {
        // Every 5 minutes, get an updated auth token.
        const token = await this.elasticAuthManager.getUpdatedToken();
        updateChemicalSearchToken(token);
        this.elasticAuthManager.setAuthToken(token);
      }, 300000);
    },
    getSearchLabel: function () {
      switch (this.chemicalSearchType) {
        case "cas_number":
          return "CAS Number";

        case "product":
          return "Product Name";

        default:
          return "Chemical Name";
      }
    },
    getPlaceholder: function () {
      switch (this.chemicalSearchType) {
        case "cas_number":
          return "Search for chemicals by CAS number";

        case "product":
          return "Search for chemicals by product name";

        default:
          return "Search for chemicals by name";
      }
    },
    chemicalLookup: async function (input, callback) {
      // See if this string needs to be searched for anyway. The pattern ../ is
      // stripped since it may be misinterpreted by the browser.
      input = input.replace(/^(\s+|\.{2,}\/|\s+)$/g, "");
      // Remove trailing physical state in parentheses or a trailing comma, since
      // that is the separator between search terms.
      input = input.replace(/\s(\((Solid|Liquid|Gas)\)|,)$/, "").trimEnd();
      if (input.length <= 2) {
        this.hasSearched = false;
        return;
      }
      this.lookupValue = input;
      this.$emit("search-term-entered", input);
      this.errors = [];
      const response = await this.elasticSearchClient
        .search(this.chemicalSearchType, input)
        .catch((error) => {
          this.hasSearched = true;
          this.$showNotification("error", "Failed to find any chemicals.");
          this.$emit("error", "Failed to find any chemicals.");
          this.errors.push("Failed to find any chemicals.");
          console.error(error); // eslint-disable-line no-console
        });
      if (response) {
        this.hasSearched = true;
      }
      // Get the matches from the ES search response.
      if (this.chemicalSearchType === "suggest") {
        this.searchResults = response?.suggest?.chemical_suggest?.[0]?.options;
      } else {
        this.searchResults = response?.hits?.hits;
      }
      return callback ? callback(this.searchResults) : this.searchResults;
    },
    updateSelected: function (value) {
      this.selectedItem = value;
      value.search = this.lookupValue;
      value.searchType = this.chemicalSearchType;
      this.$emit("chemical-lookup-selection", value, this.chemicalSearchType);
      this.$refs.autocomplete.$forceUpdate();
    },
    clearSelected: function () {
      this.selectedItem = {};
      this.hasSearched = false;
      this.$emit("chemical-lookup-clear", {}, this.chemicalSearchType);
    },
    formatResults(data) {
      let formattedData = [];
      if (data) {
        formattedData = [...data];
        Object.entries(formattedData).forEach(([index, chemicalData]) => {
          if (chemicalData.hasOwnProperty("selectedObject")) {
            formattedData[index].id =
              chemicalData.selectedObject._source?.uuid ??
              chemicalData.selectedObject._source?.chemical_uuid?.[0];
          } else {
            formattedData[index].id =
              chemicalData._source?.uuid ??
              chemicalData._source?.chemical_uuid?.[0];
          }
        });
      }
      formattedData.push({
        id: "_none_",
        _source: {
          title: ["--None of the above--"],
        },
      });
      return formattedData;
    },
    formatProductName(result) {
      let product = null;
      if (result.hasOwnProperty("_source")) {
        product = result._source;
      } else if (result.hasOwnProperty("selectedObject")) {
        product = result.selectedObject._source;
      }
      if (product?.hasOwnProperty("product_name")) {
        return (
          product.manufacturer_name[0] +
          " - " +
          product.product_name[0] +
          " (" +
          product.product_number[0] +
          ")"
        );
      }
      return result._source?.title?.[0];
    },
    formattedResultsDisplay(result) {
      if (result) {
        if (this.chemicalSearchType === "product") {
          return this.formatProductName(result);
        }

        let physicalState = "";
        let title = "";
        let cas = "";
        if (result.hasOwnProperty("selectedObject")) {
          physicalState =
            config.physicalStates[
              result.selectedObject._source?.field_chemdb_physical_state
            ];
          title =
            result?.selectedObject?.text ||
            result?.selectedObject?._source?.title?.[0];
          cas = result?.selectedObject?._source?.field_chemdb_cas_number;
        } else {
          physicalState =
            config.physicalStates[result._source?.field_chemdb_physical_state];
          title = result?.text || result._source?.title?.[0];
          cas = result?._source?.field_chemdb_cas_number;
        }
        if (physicalState) {
          title = title + " (" + physicalState + ")";
        }
        if (cas) {
          title = title + " - " + cas;
        }
        return title;
      }
    },
    handleChemsnapMatchChange() {
      this.$refs.autocomplete.display = this.chemsnapFound;
      this.$refs.autocomplete.blur();
      this.$emit("toggle-show-search-input");
    },
    updateSearch(value) {
      this.searchTerm = value;
    },
    changeSearch(type) {
      this.searchType = type;
    },
  },
  computed: {
    searchResultsLinkText() {
      return `${this.searchResultsCount} ${this.searchResultsCount > 1 ? "matches" : "match"}`;
    },
    selectedOrChooseHelpText() {
      const selectionMade = this.selectedItem?.selectedObject?.text;
      if (selectionMade) {
        return `Selected from `;
      }
      return `Choose ${this.searchResultsCount === 1 ? "this" : "from these"} `;
    },
    resultsProperty() {
      switch (this.chemicalSearchType) {
        case "cas_number":
          return "field_chemdb_cas_number";
        case "product":
          return "field_chemical_product_name";
        default:
          return "title";
      }
    },
    _status() {
      if (this.status === null && this.hasSearched) {
        if (this.chemsnapFound) {
          return "info";
        }
        if (this.errors.length > 0) {
          return "err";
        }
        if (this.searchResultsCount === 0 && this.searchTerm) {
          return "warning";
        }
      }
      return this.status;
    },
    chemsnapFound() {
      return (
        this.chemSnapData?.["Chemical name"] ||
        this.chemSnapData?.["CAS number"] ||
        this.chemSnapData?.["Product name"] ||
        this.chemSnapData?.["Product number"]
      );
    },
    searchByLabel: function () {
      return this.localChemical ? "" : "Search By";
    },
    localChemicalTitle: function () {
      if (this.localChemical.display) {
        return this.localChemical.display;
      }
      return `${this.localChemical.title} (${
        config.physicalStates[this.localChemical.field_chemdb_physical_state]
      })`;
    },
    useLocalChemical: function () {
      return (
        this.localChemical &&
        (Object.hasOwn(this.localChemical, "display") ||
          Object.hasOwn(this.localChemical, "title") ||
          Object.hasOwn(this.localChemical, "value"))
      );
    },
    searchResultsCount() {
      return this.searchResults?.length;
    },
    searchOptions() {
      const defaultChemicalSearchOptions = [
        { text: "Chemical Name", value: "suggest" },
        { text: "CAS Number", value: "cas_number" },
        { text: "Product Name or Number", value: "product" },
      ];
      const horizontalChemicalSearchOptions = [
        { text: "Name", value: "suggest" },
        { text: "CAS Number", value: "cas_number" },
        { text: "Product Name/Number", value: "product" },
      ];
      return this.horizontalOptions
        ? horizontalChemicalSearchOptions
        : defaultChemicalSearchOptions;
    },
  },
  mounted() {
    if (this.defaultLookupValue) {
      this.$set(this, "value", this.defaultLookupValue);
    }
    if (this.value) {
      this.selectedItem = this.value;
      if (this.$refs.autocomplete) {
        this.$refs.autocomplete.setResults([this.value]);
        this.$refs.autocomplete.select(this.value);
      }
    }
  },
  created() {
    this.elasticAuthManager = new ElasticsearchAuthManager(
      config.chemicalSearchAuthToken,
    );
    if (this.elasticAuthManager.authToken) {
      this.elasticSearchClient = new ElasticsearchClient(
        this.elasticAuthManager,
      );
      this.chemicalSearchType = this.chemicalSearchTypeDefault || "suggest";
      this.updateAuthToken();
    } else {
      this.$showNotification("error", "Chemical search is not available.");
      this.$showNotification("error", ErrorStateLinks);
    }
  },
  watch: {
    chemicalSearchTypeDefault: {
      immediate: true,
      handler(newValue) {
        this.$set(this, "chemicalSearchType", newValue);
      },
    },
    defaultLookupValue: {
      immediate: true,
      async handler(newValue) {
        this.$set(this, "lookupValue", newValue);
        await this.chemicalLookup(newValue);
      },
    },
  },
};
</script>

<style scoped>
.field__label.required :deep(sup) {
  font-size: 12pt;
  font-weight: bold;
  vertical-align: bottom;
}

.chemical-search-options-inline {
  display: inline-block;
  width: auto;
  margin-right: 1rem;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}

.chemical-search-options-inline-above {
  display: inline-block;
  width: auto;
  margin-right: 1rem;
}

.chemical-search-options-inline > :deep(div),
.chemical-search-options-inline-above > :deep(div) {
  display: inline;
}

.chemical-search-options-inline-above > :deep(div) {
  margin-right: 0.5rem;
}

.chemical-search-options-inline :deep(label),
.chemical-search-options-stacked :deep(label),
.chemical-search-options-stacked-above > :deep(label) {
  display: inline-block;
  margin-left: 0.0625rem;
  margin-right: 0.5rem;
  font-weight: normal;
}

.chemical-search-unnamed-slot {
  container: card / inline-size;
}

:deep(#chem-search-unlinked___BV_modal_body_) {
  padding: 0 0.5rem 0.5rem;
  margin-top: 0;
}

:deep(#chem-search-unlinked___BV_modal_header_) {
  background-color: #e9e9e9;
  padding: 0.375rem;
  vertical-align: center;
}

#chemical-search-wrapper {
  margin-top: 0.5rem;
}

.horizontal-search-by-chemical {
  padding: 0 0 0.1rem 0;
  font-family: var(--font-system-ui);
}

.horizontal-search-by-chemical
  :deep(div.chemical-options #chemical-search-type) {
  display: flex;
  flex-direction: row;
  min-width: 19rem;
}

.chemical-options :deep(div.field-label span) {
  font-weight: 500;
}

.horizontal-search-by-chemical :deep(div.custom-radio input) {
  display: none;
}

.horizontal-search-by-chemical :deep(div.custom-radio) {
  padding: 0.2rem 0.5rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.horizontal-search-by-chemical :deep(div.custom-radio:has(input:checked)) {
  border: solid 2px var(--gray-500);
  padding: 0.2rem 0.375rem;
}

:deep(.chemical-title) {
  font-size: var(--font-size-200);
  padding: 0 0 0.25rem 0;
  line-height: normal;
}

:deep(.chemical-options) {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.25rem;
  margin: 0.25rem 0;
}

#unlinked-chemical-selection {
  display: flex;
  flex-direction: row;
  align-items: center;
  font-family: var(--font-system-ui);
  font-size: var(--font-size-75);
}

.unlinked-pair {
  padding-right: 1rem;
}
</style>
